
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { FoundationalTask, GrowthTask, RecurringTask } from "./useMyTasks/types";

interface TaskWithBrand {
  brand: {
    id: string;
    name: string;
    slug?: string;
  };
}

export type FoundationalTaskWithBrand = FoundationalTask & TaskWithBrand;
export type GrowthTaskWithBrand = GrowthTask & TaskWithBrand;
export type RecurringTaskWithBrand = RecurringTask & TaskWithBrand;

export const useMyTasksAllBrands = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const fetchFoundationalTasksAllBrands = async () => {
    if (!user) {
      return [];
    }

    try {
      // Get user's growth squad IDs across all brands
      const { data: squadData } = await supabase
        .from('growth_squad')
        .select('id, brand_id')
        .eq('user_id', user.id);

      const squadIds = squadData?.map(squad => squad.id) || [];

      // Only fetch tasks assigned to user's squad
      if (squadIds.length === 0) {
        return [];
      }

      const { data, error } = await supabase
        .from('foundational_tasks')
        .select(`
          *,
          brands (
            id,
            name,
            slug
          ),
          growth_sprints!foundational_tasks_sprint_id_fkey (
            id,
            name
          )
        `)
        .in('assigned_to', squadIds)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Batch fetch owner info for assigned_to
      const assignedToIds = [...new Set((data || []).map(t => t.assigned_to).filter(Boolean))];
      let ownerMap: Record<string, { name: string; alias: string }> = {};
      if (assignedToIds.length > 0) {
        const { data: ownerData } = await supabase
          .from('growth_squad')
          .select('id, name, alias')
          .in('id', assignedToIds);
        if (ownerData) {
          ownerMap = Object.fromEntries(ownerData.map(o => [o.id, { name: o.name, alias: o.alias }]));
        }
      }
      
      return (data || []).map((task: any) => {
        const ownerInfo = task.assigned_to ? ownerMap[task.assigned_to] : undefined;
        
        return {
          ...task,
          brand: task.brands ? {
            id: task.brands.id,
            name: task.brands.name,
            slug: task.brands.slug
          } : { id: '', name: 'Unknown Brand' },
          sprint: task.growth_sprints ? {
            id: task.growth_sprints.id,
            name: task.growth_sprints.name
          } : undefined,
          owner_info: ownerInfo ? {
            name: ownerInfo.name,
            alias: ownerInfo.alias
          } : undefined
        };
      });
    } catch (error: any) {
      console.error('Error fetching foundational tasks across brands:', error);
      toast({
        title: "Error",
        description: "Failed to fetch foundational tasks",
        variant: "destructive",
      });
      throw error;
    }
  };

  const fetchGrowthTasksAllBrands = async () => {
    if (!user) {
      return [];
    }

    try {
      // Get user's growth squad IDs across all brands
      const { data: squadData } = await supabase
        .from('growth_squad')
        .select('id, brand_id')
        .eq('user_id', user.id);

      const squadIds = squadData?.map(squad => squad.id) || [];
      
      // Only fetch tasks owned by user's squad
      if (squadIds.length === 0) {
        return [];
      }

      const { data, error } = await supabase
        .from('growth_tasks')
        .select(`
          *,
          brands!fk_growth_tasks_brand (
            id,
            name,
            slug
          ),
          sales_channels!fk_growth_tasks_sales_channel (
            id,
            name
          ),
          growth_sprints!fk_growth_tasks_sprint_id (
            id,
            name
          )
        `)
        .in('owner', squadIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Batch fetch owner info
      const ownerIds = [...new Set((data || []).map(t => t.owner).filter(Boolean))];
      let ownerMap: Record<string, { name: string; alias: string }> = {};
      if (ownerIds.length > 0) {
        const { data: ownerData } = await supabase
          .from('growth_squad')
          .select('id, name, alias')
          .in('id', ownerIds);
        if (ownerData) {
          ownerMap = Object.fromEntries(ownerData.map(o => [o.id, { name: o.name, alias: o.alias }]));
        }
      }
      
      return await Promise.all((data || []).map(async (task: any) => {
        let submittedBySquad = null;
        if (task.submitted_by) {
          const { data: squadData } = await supabase
            .from('growth_squad')
            .select('name, alias, email')
            .eq('id', task.submitted_by)
            .single();
          
          if (squadData) {
            submittedBySquad = squadData;
          }
        }
        
        const ownerInfo = task.owner ? ownerMap[task.owner] : undefined;
        
        return {
          ...task,
          brand: task.brands ? {
            id: task.brands.id,
            name: task.brands.name,
            slug: task.brands.slug
          } : { id: '', name: 'Unknown Brand' },
          growth_squad: submittedBySquad,
          owner_info: ownerInfo ? {
            name: ownerInfo.name,
            alias: ownerInfo.alias
          } : undefined,
          sales_channel: task.sales_channels ? {
            id: task.sales_channels.id,
            name: task.sales_channels.name
          } : undefined,
          sprint: task.growth_sprints ? {
            id: task.growth_sprints.id,
            name: task.growth_sprints.name
          } : undefined,
          status: task.task_status || 'pending'
        };
      }));
    } catch (error: any) {
      console.error('Error fetching growth tasks across brands:', error);
      toast({
        title: "Error",
        description: "Failed to fetch growth tasks",
        variant: "destructive",
      });
      throw error;
    }
  };

  const fetchRecurringTasksAllBrands = async () => {
    if (!user) {
      return [];
    }

    try {
      // Get user's growth squad IDs across all brands
      const { data: squadData } = await supabase
        .from('growth_squad')
        .select('id, brand_id')
        .eq('user_id', user.id);

      const squadIds = squadData?.map(squad => squad.id) || [];

      // Only fetch tasks assigned to user's squad
      if (squadIds.length === 0) {
        return [];
      }

      const { data, error } = await supabase
        .from('recurring_tasks')
        .select(`
          *,
          brands (
            id,
            name,
            slug
          ),
          growth_sprints!recurring_tasks_sprint_id_fkey (
            id,
            name
          )
        `)
        .in('assigned_to', squadIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Batch fetch owner info for assigned_to
      const assignedToIds = [...new Set((data || []).map(t => t.assigned_to).filter(Boolean))];
      let ownerMap: Record<string, { name: string; alias: string }> = {};
      if (assignedToIds.length > 0) {
        const { data: ownerData } = await supabase
          .from('growth_squad')
          .select('id, name, alias')
          .in('id', assignedToIds);
        if (ownerData) {
          ownerMap = Object.fromEntries(ownerData.map(o => [o.id, { name: o.name, alias: o.alias }]));
        }
      }
      
      return (data || []).map((task: any) => {
        const ownerInfo = task.assigned_to ? ownerMap[task.assigned_to] : undefined;
        
        return {
          ...task,
          images: Array.isArray(task.images) ? task.images : (task.images ? [task.images] : []),
          attachments: Array.isArray(task.attachments) ? task.attachments : (task.attachments ? [task.attachments] : []),
          brand: task.brands ? {
            id: task.brands.id,
            name: task.brands.name,
            slug: task.brands.slug
          } : { id: '', name: 'Unknown Brand' },
          sprint: task.growth_sprints ? {
            id: task.growth_sprints.id,
            name: task.growth_sprints.name
          } : undefined,
          owner_info: ownerInfo ? {
            name: ownerInfo.name,
            alias: ownerInfo.alias
          } : undefined
        };
      });
    } catch (error: any) {
      console.error('Error fetching recurring tasks across brands:', error);
      toast({
        title: "Error",
        description: "Failed to fetch recurring tasks",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Fetch foundational tasks with React Query
  const {
    data: foundationalTasks = [],
    isLoading: isLoadingFoundational
  } = useQuery({
    queryKey: ['my-tasks-all-brands', 'foundational', user?.id],
    queryFn: fetchFoundationalTasksAllBrands,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch growth tasks with React Query
  const {
    data: growthTasks = [],
    isLoading: isLoadingGrowth
  } = useQuery({
    queryKey: ['my-tasks-all-brands', 'growth', user?.id],
    queryFn: fetchGrowthTasksAllBrands,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch recurring tasks with React Query
  const {
    data: recurringTasks = [],
    isLoading: isLoadingRecurring
  } = useQuery({
    queryKey: ['my-tasks-all-brands', 'recurring', user?.id],
    queryFn: fetchRecurringTasksAllBrands,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Enhanced refetch that invalidates all task queries
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['my-tasks-all-brands'] });
  };

  return {
    foundationalTasks,
    growthTasks,
    recurringTasks,
    isLoading: isLoadingFoundational || isLoadingGrowth || isLoadingRecurring,
    refetch,
  };
};
